import React from 'react';
import {
    Badge,
    Progress, Button,
    DropdownMenu,
    DropdownItem,
    DropdownToggle,
    UncontrolledButtonDropdown,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import * as moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTasks, faDownload, faCircleNotch, faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { crewLeadLabel } from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, ApplicationPermissions } from '../Common';
import {
    getFilterModel,
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    getServerSideRowRequest,
    TimesheetNumberCellRenderer,
    EmployeeAccountStatusRenderer,
    getMappedFilterModel,
    FlagIconCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    onFieldChange,
    toasty,
    SmallButton,
    FlexCenterRow,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { handleFormSaveError } from '../common/forms/ValidationError';
import authService from '../api-authorization/AuthorizeService';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { Can } from '../Can';
import { Alert } from '../common/forms/ModalForm';
import { Modal } from '../common/Modal';
import FlagDismissalModalBody from './FlagDismissalModalBody';
import './TimesheetManagementIndex.scss';

const getPDFExportDateMessage = (agFilterModel) => {
    // Date range must be <= 7 days
    // https://momentjs.com/docs/#/displaying/difference/
    const from = moment(agFilterModel.Date.dateFrom);
    const to = moment(agFilterModel.Date.dateTo);
    const dateDiff = Math.abs(to.diff(from, 'days'));
    const isDateOK = dateDiff <= 7;

    const ret = isDateOK ? '' : 'Exports must be for no more than 7 days.';
    return ret;
};

const getPDFExportStatusMessage = (agFilterModel) => {
    const isStatusOK = (agFilterModel.Status?.value ?? 7) == 7;

    const ret = isStatusOK
        ? ''
        : 'Exports must be for Approved timesheets only.';

    return ret;
};

const printTimesheet = (id) => {
    if (id) {
        httpClient.open(ApiRoutes.report.timesheet(id), '_self');
    }
};
const printTimesheetSetup = (id) => {
    if (id) {
        httpClient.open(ApiRoutes.report.timesheetSetup(id), '_self');
    }
};

const PrintDropdownRenderer = (props) => {
    const { data, printClicked, setupClicked } = props;

    return (
        <UncontrolledButtonDropdown size="sm">
            <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    printClicked(data.id);
                }}
            >
                <i className="fa fa-print" />
            </button>

            <DropdownToggle
                split
                color="link"
                size="sm"
                onMouseDown={(e) => e.stopPropagation()}
            />

            <DropdownMenu
                container="body"
                end
            >
                <DropdownItem
                    onClick={(e) => {
                        e.stopPropagation();
                        setupClicked(data.id);
                    }}
                >
                    <FontAwesomeIcon icon={faFilePdf} />
                    Print Setup Charges
                </DropdownItem>
            </DropdownMenu>
        </UncontrolledButtonDropdown>
    );
};

class TimesheetManagementIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();

        const params = new URLSearchParams(props.location.search);

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedDetails: [],
            showConfirmExportLargeDataset: false,
            isExcelExporting: false,
            showPDFExportModal: false,
            isPDFExporting: false,
            pdfExportModalMessage: null,
            isPDFExportAllowed: false,
            loadingPDFReport: false,
            timesheetDetailsOpen: params.has('t'),
            currentTimesheetId: params.has('t') ? params.get('t') : null,
            filterCount: 0,
            maxRows: 0,
        };

        // this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        const prevSearch = prevProps.location?.search;
        const nextSearch = this.props.location?.search;
        if (prevSearch !== nextSearch) {
            const params = new URLSearchParams(nextSearch);
            const nextId = params.has('t') ? params.get('t') : null;

            if (String(nextId) !== String(this.state.currentTimesheetId)) {
                this.openTimesheetDetails(nextId);
            }
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onChange = onFieldChange;

    onExportToExcelClicked = async () => {
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);

        const from = req?.filterModel.Date?.dateFrom;
        const to = req?.filterModel.Date?.dateTo;

        if (!from || !to) {
            toasty.error(
                'Please specify a date range when exporting Timesheet search results to excel.',
            );
        } else {
            await this.exportToExcel();
        }

        return null;
    };

    onNavBack = async () => {
        const { currentTimesheetId } = { ...this.state };
        const rows = [];
        this.state.gridOptions.api.forEachNode((node) => rows.push(node.data));
        const navArray = rows.map((x) => x.id);

        if ((navArray ?? []).length > 0) {
            let currentIndex = navArray.indexOf(currentTimesheetId);

            // TODO: Refactor this navigation logic
            // eslint-disable-next-line
            const previousIndex = --currentIndex % navArray.length;
            const id = navArray[previousIndex];

            if (id) {
                this.setState({ currentTimesheetId: id });
                await this.context.setFormOpened(true);
                this.timesheetDetailsRef.current.open(id);
            }
        }
    };

    onNavForward = async () => {
        const { currentTimesheetId } = { ...this.state };
        const rows = [];
        this.state.gridOptions.api.forEachNode((node) => rows.push(node.data));
        const navArray = rows.map((x) => x.id);

        if ((navArray ?? []).length > 0) {
            let currentIndex = navArray.indexOf(currentTimesheetId);

            // TODO: Refactor this navigation logic
            // eslint-disable-next-line
            const nextIndex = ++currentIndex % navArray.length;
            const id = navArray[nextIndex];

            if (id) {
                this.setState({ currentTimesheetId: id });
                await this.context.setFormOpened(true);
                this.timesheetDetailsRef.current.open(id);
            }
        }
    };

    async onPDFExportClick() {
        this.setState({ isPDFExporting: true });

        const { gridOptions } = this.state;

        // https://www.ag-grid.com/javascript-data-grid/grid-api/#reference-filter
        const agFilterModel = gridOptions.api.getFilterModel();
        const serverFilterModel = getFilterModel(agFilterModel);

        try {
            const response = await httpClient.post(
                ApiRoutes.timesheetManagement.export(),
                serverFilterModel,
            );
            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Timesheet Report complete');

                gridOptions.refresh();

                this.setState({
                    isPDFExporting: false,
                    showPDFExportModal: false,
                });
            }
        } catch {
            toasty.error('Error exporting timesheets');
        }
    }

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    // 2022-09-09 - M. Nicol
    // Export is allowed if the date range is <= 7 days
    // and at least has the possibility of containing Approved timesheets.
    getPDFExportMessageAndAllow() {
        const { gridOptions } = this.state;

        const agFilterModel = gridOptions.api.getFilterModel();

        // Date range must be no more than 7 days.
        // Timesheet status must be null (all) or 7 (Approved).
        // Must be for 1 dispatch location only (Removed for now - re-eanble and include in "msg" if desired.)
        // Empty message means all passed.

        const dateMessage = getPDFExportDateMessage(agFilterModel);
        const statusMessage = getPDFExportStatusMessage(agFilterModel);

        let msg = `${dateMessage} ${statusMessage}`.trim();
        const isAllowed = msg === '';
        if (isAllowed) msg = 'Export will be generated based upon search criteria entered on the timesheet screen.  If the result set is too large, the export may time out.';

        const ret = {
            message: msg,
            isAllowed,
        };

        return ret;
    }



    downloadPDFExport = async (id) => {
        if (id) {
            this.setState({ loadingPDFReport: true });

            await httpClient
                .downloadFile(
                    ApiRoutes.timesheetManagement.download(id),
                    null,
                    'Timesheet.pdf',
                )
                .catch(this.handleSaveError);

            this.setState({ loadingPDFReport: false });
        }
    };

    exportToExcel = async () => {
        this.setState({
            showConfirmExportLargeDataset: false,
            isExcelExporting: true,
        });
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);
        const fileName = `TimesheetManagementSearch_${moment().format(
            'YYYYMMDD_hhmmss',
        )}.xlsx`;
        try {
            await httpClient.downloadFile(
                ApiRoutes.timesheetManagement.excelExport(),
                req,
                fileName,
            );
        } catch (err) {
            toasty.error(`Error exporting data: ${err}`);
        } finally {
            this.setState({ isExcelExporting: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    openTimesheetDetails = async (id) => {
        if (id) {
            this.setState({ currentTimesheetId: id });
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    openFlagModal = (row) => {
        const timesheetId = row?.id;
        if (!timesheetId) return;

        Modal.show({
            title: 'Timesheet Flag',
            onSubmit: async (values) => {
                const flagId = Number(values?.flagId);
                if (!flagId) {
                    toasty.error('No flag selected to dismiss.');
                    return;
                }
                try {
                    await httpClient.post(
                        ApiRoutes.timesheetManagement.dismissFlag(),
                        { flagId, timesheetId, notes: values.notes || null },
                    );
                    toasty.success('Flag dismissed.');
                    this.state.gridOptions.refresh();
                } catch (ex) {
                    console.error('Dismiss flag failed', ex);
                    toasty.error(ex?.message || 'Failed to dismiss flag.');
                }
            },
            SaveButtonProps: {
                label: 'Dismiss Flag',
                savingLabel: 'Dismissing',
                color: 'danger',
            },
            body: (props) => (
                <FlagDismissalModalBody
                    timesheetId={timesheetId}
                    {...props}
                />
            ),
        });
    };

    fetchTimesheetCounts = debounce(async (gridOptions) => {
        const result = await httpClient.post(
            ApiRoutes.timesheetManagement.searchCounts(),
            { filterModel: getMappedFilterModel(gridOptions.api.getFilterModel(), gridOptions) },
        );

        this.setState({
            scoreboardData: result,
            maxRows: result.maxRows,
        });
    }, 500);

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        const { tenant, permissions } = this.context;
        const timesheetFlagEnabled = tenant && !!tenant.tenantSettings.timesheetFlagEnabled && permissions.includes(ApplicationPermissions.timesheet_equipment_audit_logs_view);
        const enableWorkCardValidation = tenant && !!tenant.tenantSettings.enableWorkCardValidation;
        const enforceContractUnionWork = tenant && !!tenant.tenantSettings.enforceContractUnionWork;
        const displayJobTypeInTimesheetManagement = tenant && !!tenant.tenantSettings.displayJobTypeInTimesheetManagement;

        if (isAuthenticated) {
            const [dispatchLocations, timesheetStatuses, timesheetFlagTypes, jobTypes, unionWorkTypes] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
                httpClient.js(ApiRoutes.typeAheads.timesheetStatuses()),
                httpClient.js(ApiRoutes.typeAheads.flagTypes()),
                httpClient.js(ApiRoutes.typeAheads.jobTypes()),
                enforceContractUnionWork ? httpClient.js(ApiRoutes.typeAheads.contractUnionWorkTypesUsedByTenant()) : Promise.resolve([]),
            ]);

            const gridOptions = createGridOptions(this);
            const context = this;

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                iconRenderer: IconCellRenderer,
                timesheetNumberRenderer: TimesheetNumberCellRenderer,
                variableLinkRenderer: VariableLinkCellRenderer,
                employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
                flagIconRenderer: FlagIconCellRenderer,
                PrintDropdownRenderer,
            };

            // this allows row colors to be set
            gridOptions.rowClassRules = {
                'ag-row-warning': (params) => (params.data ?? {}).isClientCancelled === true
                    || (params.data ?? {}).isSchedulingErrorCancelled === true,
                'ag-row-danger': (params) => (
                    (params.data ?? {}).isEmergency === true
                    || ((params.data ?? {}).isZeroHours === true
                        && params?.data?.status !== 'New')
                ),
            };

            const dispatchFilterParams = {
                suppressFilterButton: true,
                options: dispatchLocations,
                optionsLabel: 'label',
                optionsValue: 'value',
            };
            const timesheetStatusFilterParams = {
                suppressFilterButton: true,
                options: timesheetStatuses,
                optionsLabel: 'label',
                optionsValue: 'value',
            };
            // Filter out Workcard Unreadable flag (Id 4) if EnableWorkCardValidation is disabled
            const filteredFlagTypes = enableWorkCardValidation
                ? timesheetFlagTypes
                : timesheetFlagTypes.filter((f) => f.value !== 4);

            const timesheetFlagFilterParams = {
                suppressFilterButton: true,
                options: filteredFlagTypes,
                optionsLabel: 'label',
                optionsValue: 'value',
            };
            const timesheetTypeFilterParams = {
                suppressFilterButton: true,
                options: [
                    { value: 1, label: 'Regular Flagging' },
                    { value: 2, label: 'Non-Flagging' },
                    { value: 3, label: 'Prevailing Wage' },
                ],
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const getTimesheetType = (row) => {
                if (!row?.data) return '';

                if (row.data.isPrevailingWage) return 'Prevailing Wage';
                if (row.data.isNonFlagging) return 'Non-Flagging';

                return 'Regular Flagging';
            };

            const unionWorkTypeFilterParams = {
                suppressFilterButton: true,
                options: [{ value: -1, label: 'Non Flagging' }, ...unionWorkTypes],
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const jobTypeFilterParams = {
                suppressFilterButton: true,
                options: jobTypes,
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const statusValueGetter = (row) => {
                if (row.data) {
                    const isAddStatus = !!row.data.isZeroHours
                        || !!row.data.isEmergency
                        || !!row.data.isClientCancelled
                        || !!row.data.isSchedulingErrorCancelled;
                    let addStatus = '';
                    if (row.data.isEmergency) addStatus += ' - Emergency';
                    if (row.data.isZeroHours) addStatus += ' - Zero Hours';
                    if (row.data.isClientCancelled) addStatus += ' - Client Cancel';
                    if (row.data.isSchedulingErrorCancelled) addStatus += ' - Scheduling Error';
                    if (row.data.isNoDetails) addStatus += ' - No Charges';

                    const status = `${row.data.status}${isAddStatus ? addStatus : ''}`;
                    return status;
                }

                return '';
            };

            gridOptions.columnDefs = [
                {
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    hide: true,
                    flex: 1,
                    maxWidth: 35,
                    minWidth: 35,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'Date',
                    headerName: 'Date',
                    field: 'date',
                    sortable: true,
                    minWidth: 120,
                    sort: { direction: 'desc', priority: 0 },
                    filter: 'agDateColumnFilter',
                    filterParams: DateFilterDefaults,
                    valueFormatter: (params) => params.value ? moment(params.value).format('YYYY-MM-DD') : '',
                },
                {
                    colId: 'CustomerName',
                    headerName: 'Customer Name',
                    field: 'customerName',
                    sortable: true,
                    flex: 2,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    valueGetter: (row) => {
                        if (row.data) {
                            const { customerName } = row.data;
                            const subcontractorName = row.data.subcontractorName
                                ? ` - ${row.data.subcontractorName}`
                                : '';
                            const operationsCenterCompanyName = row.data.operationsCenterCompanyName
                                ? ` (${row.data.operationsCenterCompanyName})`
                                : '';

                            return `${customerName}${subcontractorName}${operationsCenterCompanyName}`;
                        }

                        return '';
                    },
                },
                {
                    colId: 'CrewLead',
                    headerName: `${crewLeadLabel(tenant?.tenantSettings)} // Employee`,
                    field: 'crewLead',
                    sortable: true,
                    flex: 2,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'employeeAccountStatusRenderer',
                    cellRendererParams: {
                        nameField: 'crewLead',
                    },
                },
                {
                    colId: 'TimesheetNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                    headerName: 'Timesheet #',
                    field: 'timesheetNumber',
                    sortable: true,
                    flex: 1.5,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'timesheetNumberRenderer',
                },
                enforceContractUnionWork ? {
                    colId: 'WorkTypeId',
                    headerName: 'Work Type',
                    field: 'workTypeName',
                    sortable: false,
                    flex: 1.5,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: unionWorkTypeFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: unionWorkTypeFilterParams,
                } : {
                    colId: 'Type',
                    headerName: 'Work Type',
                    sortable: false,
                    flex: 1.5,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: timesheetTypeFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: timesheetTypeFilterParams,
                    valueGetter: getTimesheetType,
                },
                {
                    colId: 'WorkOrderNumber',
                    headerName: 'Work Order',
                    field: 'workOrderNumber',
                    sortable: true,
                    flex: 1.5,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'DispatchLocation',
                    headerName: 'Dispatching',
                    sortable: true,
                    flex: 1.5,
                    field: 'dispatchOfficeName',
                    tooltipField: 'dispatchOfficeName',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: dispatchFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: dispatchFilterParams,
                },
                {
                    colId: 'Status',
                    headerName: 'Status',
                    field: 'status',
                    sortable: true,
                    flex: 2,
                    tooltipField: 'status',
                    cellRenderer: 'variableLinkRenderer',
                    cellRendererParams: (r) => ({
                        clicked: this.downloadPDFExport,
                        isLink: !!r.data?.timesheetExportId,
                        title: 'Download Export',
                        idField: 'timesheetExportId',
                        valueGetter: statusValueGetter,
                    }),
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: timesheetStatusFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: timesheetStatusFilterParams,
                    // 2022-09-09 - M. Nicol - uncomment below and comment out
                    // cellRenderer and cellRenderedParams.
                    // ,valueGetter: statusValueGetter
                },
                {
                    colId: 'Id',
                    minWidth: 65,
                    headerName: 'View',
                    sortable: false,
                    cellRenderer: 'iconRenderer',
                    cellRendererParams: {
                        clicked: this.openTimesheetDetails,
                        idField: 'id',
                        iconClass: 'fa-eye timesheet-opener-btn',
                    },
                },
                {
                    colId: 'Id',
                    minWidth: 65,
                    headerName: 'Print',
                    sortable: false,
                    suppressMenu: true,
                    cellRendererSelector: (params) => {
                        if (!params.data?.requiresWorkflowNotes) {
                            return {
                                component: 'iconRenderer',
                                params: {
                                    clicked: printTimesheet,
                                    idField: 'id',
                                    iconClass: 'fa-print',
                                },
                            };
                        }

                        return {
                            component: 'PrintDropdownRenderer',
                            params: {
                                printClicked: printTimesheet,
                                setupClicked: printTimesheetSetup,
                            },
                        };
                    },
                },
            ];

            if (timesheetFlagEnabled) {
                const statusIndex = gridOptions.columnDefs.findIndex((c) => c.colId === 'Status');

                const col = {
                    colId: 'Flag',
                    headerName: 'Flag',
                    field: 'flag',
                    flex: 1,
                    minWidth: 65,
                    sortable: false,
                    tooltipComponentParams: { color: '#ececec' },
                    cellRenderer: 'flagIconRenderer',
                    cellRendererParams: {
                        bodyField: 'flag',
                        excludeFlags: enableWorkCardValidation ? [] : ['Workcard Unreadable'],
                        onIconClick: this.openFlagModal,
                    },
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: timesheetFlagFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: timesheetFlagFilterParams,
                };
                if (statusIndex) {
                    gridOptions.columnDefs.splice(statusIndex + 1, 0, col);
                }
            }

            if (displayJobTypeInTimesheetManagement) {
                const statusIndex = gridOptions.columnDefs.findIndex((c) => c.colId === 'Status');

                const col = {
                    colId: 'JobType',
                    headerName: 'Job Type',
                    field: 'jobTypeName',
                    sortable: true,
                    flex: 1.5,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: jobTypeFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: jobTypeFilterParams,
                };
                if (statusIndex) {
                    gridOptions.columnDefs.splice(statusIndex + 1, 0, col);
                }
            }
            // API returns an object with counts as well as rowData for the grid.
            // Save counts to the state, and return the rowData to the grid.
            gridOptions.postProcessData = (data) => {
                context.setState({
                    rowData: data,
                });

                return { ...data.result };
            };

            gridOptions.onFilterChanged = () => {
                void context.fetchTimesheetCounts(gridOptions);
            };

            // On the grid's first load, we want to apply the fsilters immediately from the state population,
            // before the API call is made.
            gridOptions.onGridReady = (params) => {
                // Calc past 61 days
                const start = moment(moment().subtract(61, 'days')).format(
                    'YYYY-MM-DD',
                );
                const end = moment(moment().subtract(1, 'days')).format(
                    'YYYY-MM-DD',
                );

                const filterInstance = gridOptions.api.getFilterInstance('Date');
                filterInstance.setModel({
                    type: 'inRange',
                    dateFrom: start,
                    dateTo: end,
                });
                gridOptions.api.onFilterChanged();

                params.api.setDatasource(context.state.dataSource);
                context.setState({ gridApi: params.api });

                void this.fetchTimesheetCounts(gridOptions);
            };

            gridOptions.setStatus = (status) => {
                this.setState({ ...status });
            };

            const dataSource = createDataSource(
                ApiRoutes.timesheetManagement.search(),
                gridOptions,
            );

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                isRejectingTimesheet: false,
            });
        }
    };

    toggleRejectTimesheetModal = () => {
        const { showRejectTimesheetModal } = this.state;
        this.setState({ showRejectTimesheetModal: !showRejectTimesheetModal });
    };

    showPDFExportModal = () => {
        const msgAllow = this.getPDFExportMessageAndAllow();

        Modal.show({
            title: 'Export Timesheets',
            onSubmit: () => this.onPDFExportClick(),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
                disabled: !msgAllow.isAllowed,
            },
            body: () => msgAllow.message,
        });
    };

    downloadStatusHistoryReport = async () => {
        const { gridOptions } = this.state;
        this.setState({ isExcelExporting: true });
        const agFilterModel = gridOptions.api.getFilterModel();
        const serverFilterModel = getFilterModel(agFilterModel);

        try {
            const fileName = `TimesheetStatusHistoryReport_${moment().format(
                'YYYY-M-D_hhmmss',
            )}.xlsx`;

            await httpClient.downloadFile(
                ApiRoutes.timesheetManagement.timesheetStatusHistoryReport(),
                serverFilterModel,
                fileName,
            );
            toasty.success('Downloaded status history report file successfully');
        } catch {
            toasty.error('Error in Downloading Status History Report');
        } finally {
            this.setState({ isExcelExporting: false });
        }
    };

    render() {
        if (this.state.loading) return <Progress />;

        const {
            rowData,
            gridOptions,
            isExcelExporting,
            scoreboardData,
            loadingPDFReport,
        } = { ...this.state };

        let counts = {
            notSubmitted: 0,
            operationsReview: 0,
            rejected: 0,
            submitted: 0,
            payrollRejected: 0,
        };
        if (scoreboardData) {
            counts = scoreboardData;
        }

        const countNotSubmittedColor = counts.notSubmitted === 0 ? 'success' : 'warning';
        const countRejectedColor = counts.rejected === 0 ? 'success' : 'danger';
        const countOperationsReviewColor = counts.operationsReview === 0 ? 'success' : 'danger';
        const countSubmittedColor = counts.submitted === 0 ? 'success' : 'warning';
        const countPayrollRejectedColor = counts.payrollRejected === 0 ? 'success' : 'danger';

        const { tenant } = this.context;
        const timesheetExportEnabled = tenant && !!tenant.tenantSettings.timesheetExportEnabled;

        return (
            <>
                <PageWrap>
                    <PageHeading>
                        <FontAwesomeIcon
                            icon={faTasks}
                            className="mr-2 text-muted"
                        />
                        <span>Timesheet Management</span>
                        {timesheetExportEnabled && (
                            <Can
                                I="export"
                                a="timesheet"
                            >
                                <span
                                    style={{
                                        float: 'right',
                                        position: 'relative',
                                        top: '-5px',
                                    }}
                                >
                                    <Button
                                        size="sm"
                                        style={{ marginRight: '10px' }}
                                        color="danger"
                                        onClick={() => this.showPDFExportModal()}
                                    >
                                        Export
                                    </Button>
                                </span>
                            </Can>
                        )}
                        <span
                            style={{
                                float: 'right',
                                position: 'relative',
                                top: '-5px',
                            }}
                        >
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                disabled={!!this.state.saving}
                                onClick={() => this.downloadStatusHistoryReport()}
                            >
                                Status History Report
                            </Button>
                        </span>
                    </PageHeading>

                    <FlexCenterRow
                        className="pb-3 pt-2"
                        id="timesheetWorkflowStatus"
                    >
                        <span
                            style={{ minWidth: '100px' }}
                            className="mr-2 text-muted font-weight-bold p-2"
                        >
                            Field Actions:
                        </span>
                        <Badge
                            color={countNotSubmittedColor}
                            style={{ minWidth: '100px' }}
                            className="mr-3 p-2"
                        >
                            Not Submitted
                            <span className="ml-2">{counts.notSubmitted}</span>
                        </Badge>
                        <Badge
                            color={countRejectedColor}
                            style={{ minWidth: '100px' }}
                            className="mr-3 p-2"
                        >
                            Rejected
                            <span className="ml-2">{counts.rejected}</span>
                        </Badge>

                        <Badge
                            color={countOperationsReviewColor}
                            style={{ minWidth: '100px' }}
                            className="mr-3 p-2"
                        >
                            Operations Review
                            <span className="ml-2">{counts.operationsReview}</span>
                        </Badge>

                        <span
                            style={{ minWidth: '100px' }}
                            className="mr-2 text-muted font-weight-bold p-2"
                        >
                            Office Actions:
                        </span>
                        <Badge
                            color={countSubmittedColor}
                            style={{ minWidth: '100px' }}
                            className="mr-3 p-2"
                        >
                            Submitted
                            <span className="ml-2">{counts.submitted}</span>
                        </Badge>
                        <Badge
                            color={countPayrollRejectedColor}
                            style={{ minWidth: '100px' }}
                            className="mr-3 p-2"
                        >
                            Billing Payroll Rejected
                            <span className="ml-2">
                                {counts.payrollRejected}
                            </span>
                        </Badge>
                    </FlexCenterRow>

                    <DataGridToolbar
                        entity="Timesheet Management"
                        gridApi={this.state.gridApi}
                        dataSource={this.state.dataSource}
                        hideAdd
                        hideExcelButton
                        gridOptions={this.state.gridOptions}
                    >
                        <SmallButton
                            disabled={!!this.state.saving}
                            onClick={this.onExportToExcelClicked}
                        >
                            <i className="fa fa-file-excel fa-lg" />
                            <span className="ml-2 small-viewport-hide">
                                Export to Excel
                            </span>
                        </SmallButton>
                    </DataGridToolbar>

                    {!!loadingPDFReport && <CustomCircularProgress />}

                    {!loadingPDFReport && (
                        <DataGrid
                            domLayout="normal"
                            rowData={rowData}
                            gridOptions={gridOptions}
                            gridStatus={{ maxRows: this.state.maxRows, filterCount: this.state.filterCount }}
                        />
                    )}
                </PageWrap>
                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    currentTimesheetId={this.state.currentTimesheetId}
                    toggleShow={(open) => this.setState({ timesheetDetailsOpen: open })}
                    onClose={this.onTimesheetDetailsClosed}
                    onAccept={this.onAcceptTimesheet}
                    onReject={this.onRejectTimesheet}
                    isTimesheetForm={false}
                    isTimesheet
                    isPayroll={false}
                    isBilling={false}
                    showNav
                    onNavBack={this.onNavBack}
                    onNavForward={this.onNavForward}
                />

                <Alert isOpen={isExcelExporting}>
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Exporting...
                </Alert>
            </>
        );
    }
}

export default TimesheetManagementIndex;
